// The Students table's column set and how each cell reads.
//
// The column set is MMS's, less its Reminders column: a student has no
// reminder ticks (#792), and a family contact's are on the Family Contacts
// tab. The toggle matters: which columns a studio turns on is how one table
// serves very different businesses — a classical teacher wants Repertoire and
// Skill Level, a high-volume studio wants Make-Up Credits and Balance.
//
// `on` is whether a column starts visible; `locked` is one that cannot be
// turned off, since a table of students with no student name is useless.
//
// The Make-Up Credits cell can be `null` for a student without a lesson length
// (a credit is a length of time, #352), and its dash is written once in the
// domain (`formatCredits`).
import { formatCredits } from '../../domain/makeup';

export const ALL_COLUMNS = [
  { id: 'student', label: 'Student', locked: true },
  // MMS's "Student Contact" shows the student's own email or number; a
  // student has neither here (#792), so the column is the family's billing
  // contact's, under its own name. The id is kept: it is the stored column
  // choice.
  { id: 'contact', label: 'Family Contact', on: true },
  { id: 'family', label: 'Family', on: true },
  { id: 'notes', label: 'Notes', on: true },
  { id: 'instrumentTags', label: 'Instrument Tags', on: true },
  { id: 'groupTags', label: 'Group Tags', on: true },
  { id: 'nextLesson', label: 'Next Lesson', on: true },
  { id: 'makeup', label: 'Make-Up Credits', on: true },
  { id: 'age', label: 'Age', on: true },
  { id: 'birthday', label: 'Birthday' },
  { id: 'school', label: 'School' },
  { id: 'skillLevel', label: 'Skill Level' },
  { id: 'referrer', label: 'Referrer' },
  { id: 'studentSince', label: 'Student Since' },
  { id: 'price', label: 'Price' },
  { id: 'duration', label: 'Duration' },
  { id: 'category', label: 'Category' },
  { id: 'gender', label: 'Gender' },
];

// ── The student status set: five, and this is the only list of them ─────────
//
// MMS's five, `lead` included (#177), with Lead's chip colours #604274 on
// #F9E5FF. The set is load-bearing; the order is not: MMS orders the five
// differently in its `By Status` filter, its bulk `Set Status` menu and its Add
// Student wizard, so one order is used everywhere — MMS's Edit Student
// drawer's (#120).
//
// One list, shared by this file, the wizard step and the edit drawer, so a
// status cannot be missing from one of them. The labels live here too:
// "Waiting List" is MMS's word for `waiting`.
export const STATUSES = ['active', 'trial', 'waiting', 'lead', 'inactive'];

// What each status is called on a control. `StatusBadge` does not use this —
// it capitalises the stored word, so a badge and a filter option read the
// same — but every picker does.
export const STATUS_LABELS = Object.freeze({
  active: 'Active',
  trial: 'Trial',
  waiting: 'Waiting List',
  lead: 'Lead',
  inactive: 'Inactive',
});

// What each Student Type is called (#587): the stored value is the lowercase
// id the radios write, shown as "Adult" as MMS and our radios say it. A value
// outside the set is shown as stored rather than blanked: it is still what the
// document says.
export const STUDENT_TYPE_LABELS = Object.freeze({
  child: 'Child',
  adult: 'Adult',
});

export function studentTypeLabel(value) {
  if (!value) return null;
  return STUDENT_TYPE_LABELS[value] || value;
}

// A stored date-only string in the studio's short date (#587) — `12.08.2026`
// in a German Region. `formatDate` is the Region's (`useDateFormat()`), passed
// in because this file is not a component. A value it cannot read as a date
// comes back as stored; an absent one is null, so the caller's dash shows.
export function shortDateOrStored(value, formatDate) {
  if (!value) return null;
  return (formatDate && formatDate(value)) || value;
}

// The columns a studio sees before choosing. The status filter beside them
// defaults to Active, as MMS's `By Status` does (src/hooks/useStudentList.js).

export const defaultColumnIds = () =>
  ALL_COLUMNS.filter((c) => c.locked || c.on).map((c) => c.id);

// Whole years since a birthday, or null if there isn't a usable one. Compared
// on month and day rather than by dividing a millisecond difference, which
// drifts by a day across leap years.
export function ageFrom(birthday) {
  if (!birthday) return null;
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
  return a;
}

// What one cell says. `ctx` carries the things a student document does not know
// about itself — its family and the family's billing contact, its make-up
// balance, when it is next taught, and how this studio writes money.
//
// `ctx.formatMoney` rather than an import: currency is derived from the
// studio's locale (src/domain/locale.js), and this file is not a component.
// StudentsPage puts it in the context with the other things a cell cannot
// know about itself.
//
// The Student column is not here: it renders a link and a status pill rather
// than text, so the table builds it directly.
export function cellText(student, colId, ctx) {
  const s = student;
  switch (colId) {
    case 'contact': {
      const c = s.familyId ? ctx.billingContact?.(s.familyId) : null;
      return c?.email || c?.mobile || '–';
    }
    // Looked up in the families collection rather than read off the student's
    // own lastName: a child does not always share the account holder's
    // surname, and a student with no family says so.
    case 'family': return s.familyId ? ctx.familyName(s.familyId) : '–';
    // The whole string. The sidebar's Notes card clamps to five lines behind
    // `Show More` as MMS does there (#136); what MMS does with a long note in
    // this column is unrecorded, so the cell does not acquire a truncation
    // rule of its own. A 2,000-character note makes a tall row (#136).
    case 'notes': return s.note || '–';
    // Two sources (#138): which managed tags of that kind the student is in,
    // and the student document's own `instrumentTags` / `groupTags` strings,
    // which a studio keeps until `scripts/migrate-tags.mjs` turns them into
    // records (#328). The union is folded on `tagKey`, so a student in the
    // "Piano" record who also carries the string "piano" reads as one tag;
    // managed labels win the spelling, because a record is what a teacher can
    // rename.
    case 'instrumentTags': return ctx.tagLabels(s, 'instrument') || '–';
    case 'groupTags': return ctx.tagLabels(s, 'group') || '–';
    case 'nextLesson': return ctx.nextLesson(s.id) || '–';
    case 'makeup': {
      // The student's own lesson length is the divisor (#352): a credit is a
      // length of time, so the same stored minutes read differently for two
      // students. `formatCredits` dashes a null total rather than printing
      // `Total: null`. The figures are in the studio's Region, `1,5` in
      // Germany (#549).
      const m = ctx.makeupSummary(s.id, s.durationMin);
      const d = ctx.decimalSeparator;
      return `Total: ${formatCredits(m.total, d)} · Available: ${formatCredits(m.available, d)}`
        + ` · Booked: ${formatCredits(m.booked, d)}`;
    }
    case 'age': { const a = ageFrom(s.birthday); return a == null ? '–' : String(a); }
    case 'birthday': return s.birthday || '–';
    case 'school': return s.school || '–';
    case 'skillLevel': return s.skillLevel || '–';
    case 'referrer': return s.referrer || '–';
    case 'studentSince': return shortDateOrStored(s.studentSince, ctx.formatDate) || '–';
    case 'price': return s.pricingMode === 'none' ? 'No charge' : ctx.formatMoney(s.rateMinor || 0);
    // `–` where the document has no duration: the field is required at both
    // forms and in firestore.rules (#352), and `studentDurationMin` invents
    // no default.
    case 'duration': return s.durationMin == null ? '–' : `${s.durationMin} min`;
    case 'category': return s.categoryLabel || 'Lesson';
    case 'gender': return s.gender || '–';
    default: return '–';
  }
}
